import { Box, Icon as CIcon, Flex, type FlexProps, HStack, type StackProps, Text, VStack } from "@chakra-ui/react";
import { Children, type FC, type ReactNode } from "react";
import { useNavigate } from "react-router";

import { Paper } from "@equidefi/ui";
import type { PaperProps } from "@equidefi/ui/Paper/PaperTheme";
import { Icon } from "@equidefi/ui/icon";

import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

type ReviewContentProps = StackProps & {
  label?: string;
  value?: ReactNode;
};

type ReviewSectionProps = FlexProps & {
  children: ReactNode;
  page?: string;
  isEditable?: boolean;
  returnTo?: string;
};

type ReviewCardProps = PaperProps & {
  title: string;
  children?: ReactNode;
  fallback?: ReactNode;
};

const LockView = () => {
  return (
    <Flex color="gray.500">
      <CIcon as={Icon.Lock} width={5} height={5} />
    </Flex>
  );
};

const EditView: FC<Omit<ReviewSectionProps, "children">> = ({ page, returnTo = "kyc" }) => {
  const navigate = useNavigate();
  const { investment, offering } = useWorkflowContext();

  return (
    <Flex
      direction="row"
      cursor="pointer"
      h="full"
      align="center"
      color="equidefi.blue"
      onClick={() => {
        navigate(`/offerings/${offering.slug}/${investment.id}/${page}`, {
          state: {
            returnTo,
            skip_transition: true,
            refresh: true,
          },
        });
      }}>
      <CIcon as={Icon.Edit} w="5" h="5" />
      <Text ml={3}>Edit</Text>
    </Flex>
  );
};

export const ReviewContent: FC<ReviewContentProps> = ({ label, children, ...rest }) => {
  return (
    <HStack gap={0} minW="0" w="full" {...rest}>
      {label && (
        <Text whiteSpace="nowrap" fontWeight="bold">
          {label}
        </Text>
      )}
      {label && children && <Text fontWeight="bold">: </Text>}
      {children && (
        <Text ml={label ? 2 : 0} isTruncated>
          {children}
        </Text>
      )}
    </HStack>
  );
};

export const ReviewSection: FC<ReviewSectionProps> = ({ children, page, returnTo, isEditable = true, ...rest }) => {
  return (
    <Flex
      direction="row"
      align="center"
      justifyContent="space-between"
      h="full"
      w="full"
      borderBottom="1px"
      borderColor="gray.100"
      py="4"
      _first={{
        pt: 1,
      }}
      _last={{
        borderBottom: "0",
        pb: 0,
      }}
      {...rest}>
      <VStack align="start" gap={0} minW="0" flex="1">
        {children}
      </VStack>
      <Box flexShrink={0}>{isEditable ? <EditView page={page} returnTo={returnTo} /> : <LockView />}</Box>
    </Flex>
  );
};

const ReviewCard: FC<ReviewCardProps> = ({ title, children, fallback = "N/A", ...rest }) => {
  return (
    <Paper p={6} mb="4" w="full" {...rest}>
      <Text fontSize="lg" mb="3" fontWeight="bold">
        {title}
      </Text>
      {Children.count(children) > 0 ? children : fallback}
    </Paper>
  );
};

export default ReviewCard;
